import { Formik, Form } from "formik";
import { FormModel } from "../types/FormTypes";
import { useAtom } from "jotai";
import { formDataAtom } from "../App";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useRef } from "react";
import useFocusOnLoad from "../hooks/useFocusOnLoad";
import { WITH_APP } from "./TicketType";

import {
  Box,
  InputText,
  Button,
  handleSubmitClick,
  ErrorList,
  Progressbar,
  Heading,
} from "@ogt/ui";

export const ADULT = "adult";
export const NOT_ADULT = "not_adult";
export const APPEAL_FOR_MYSELF = "appelformyself";
export const PARENT = "parent";
export const FIDUCIARY = "fiduciary";
export const HAVE_FIDUCIARY = "havefiduciary";

export default function ContactDetails() {
  const h2Ref = useFocusOnLoad<HTMLHeadingElement>();
  const [formData, setFormData] = useAtom(formDataAtom);
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const progress = 88;
  return (
    <div>
      <Formik<FormModel>
        onSubmit={(values) => {
          setFormData({
            ...formData,
            ...values,
          });

          navigate("/sammanfattning");
        }}
        initialValues={{
          ...formData,
          phone:
            formData.ticketType === WITH_APP
              ? formData.phone || formData.ticketNumber || ""
              : formData.phone || "",
        }}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={Yup.object().shape({
          firstname: Yup.string().when(["agecheck", "appealforwho"], {
            is: (agecheck: string, appealforwho: string) =>
              agecheck === ADULT && appealforwho === APPEAL_FOR_MYSELF,
            then: (schema) => schema.required("Vänligen fyll i förnamn"),
            otherwise: (schema) => schema.notRequired(),
          }),

          lastname: Yup.string().when(["agecheck", "appealforwho"], {
            is: (agecheck: string, appealforwho: string) =>
              agecheck === ADULT && appealforwho === APPEAL_FOR_MYSELF,
            then: (schema) => schema.required("Vänligen fyll i efternamn"),
            otherwise: (schema) => schema.notRequired(),
          }),

          phone: Yup.string().when(["agecheck", "appealforwho"], {
            is: (agecheck: string, appealforwho: string) =>
              agecheck === ADULT && appealforwho === APPEAL_FOR_MYSELF,
            then: (schema) => schema.required("Vänligen fyll i telefonnummer"),
            otherwise: (schema) => schema.notRequired(),
          }),

          email: Yup.string().when(["agecheck", "appealforwho"], {
            is: (agecheck: string, appealforwho: string) =>
              agecheck === ADULT && appealforwho === APPEAL_FOR_MYSELF,
            then: (schema) =>
              schema
                .required(
                  "Vänligen fyll i mejladress till vårdnadshavare, god man eller ombud."
                )
                .email("Vänligen ange en korrekt mejladress"),
            otherwise: (schema) => schema.notRequired(),
          }),

          reporterEmail: Yup.string().when(["agecheck", "appealforwho"], {
            is: (agecheck: string, appealforwho: string) =>
              agecheck === NOT_ADULT ||
              (agecheck === ADULT &&
                [FIDUCIARY, HAVE_FIDUCIARY, PARENT].includes(appealforwho)),
            then: (schema) =>
              schema
                .required("Vårdnashavare fyll i resenärens mejladress")
                .email("Vänligen ange en korrekt mejladress"),
            otherwise: (schema) => schema.notRequired(),
          }),
        })}
      >
        {({ errors, values }) => (
          <>
            <div className="flex justify-end items-center">
              <div className="w-40 w-full max-w-xs mb-4">
                <Progressbar percentage={progress} />
              </div>
            </div>
            <Form noValidate>
              <Box>
                <Heading
                  headingRef={h2Ref}
                  focusable
                  id="contactDetailsHeading"
                  progress={progress}
                >
                  Kontaktuppgifter
                </Heading>
                {values.agecheck === ADULT &&
                  values.appealforwho === APPEAL_FOR_MYSELF && (
                    <>
                      <Heading as="h3" id="travelerHeading">
                        Resenär
                      </Heading>

                      {Object.keys(errors).length > 0 && (
                        <ErrorList errors={errors} />
                      )}

                      <InputText
                        name="firstname"
                        label={
                          <>
                            Förnamn <span>(Obligatoriskt)</span>
                          </>
                        }
                        type="text"
                        autocomplete={"given-name"}
                      />

                      <InputText
                        name="lastname"
                        label={
                          <>
                            Efternamn <span>(Obligatoriskt)</span>
                          </>
                        }
                        type="text"
                        autocomplete={"family-name"}
                      />

                      <InputText
                        name="phone"
                        label={
                          <>
                            Telefonnummer <span>(Obligatoriskt)</span>
                          </>
                        }
                        type="text"
                        autocomplete={"text"}
                        numbersOnly
                      />

                      <InputText
                        name="email"
                        label={
                          <>
                            Mejladress <span>(Obligatoriskt)</span>
                          </>
                        }
                        type="email"
                        autocomplete={"email"}
                      />
                    </>
                  )}

                {(values.agecheck === NOT_ADULT ||
                  (values.agecheck === ADULT &&
                    [FIDUCIARY, HAVE_FIDUCIARY, PARENT].includes(
                      values.appealforwho
                    ))) && (
                  <>
                    <p>
                      Fyll i kontaktuppgifter både till resenären och till en
                      vårdnadshavare/förälder, god man eller ett ombud.{" "}
                    </p>

                    <Heading
                      as="h3"
                      className="mb-6"
                      id="haveFiduciaryTravelerHeading"
                    >
                      Resenär
                    </Heading>

                    {Object.keys(errors).length > 0 && (
                      <ErrorList errors={errors} />
                    )}

                    <InputText
                      name="firstname"
                      label={
                        <>
                          Resenärens förnamn <span>(Obligatoriskt)</span>
                        </>
                      }
                      type="text"
                      autocomplete={"given-name"}
                    />

                    <InputText
                      name="lastname"
                      label={
                        <>
                          Resenärens efternamn <span>(Obligatoriskt)</span>
                        </>
                      }
                      type="text"
                      autocomplete={"family-name"}
                    />

                    <InputText
                      name="phone"
                      label={
                        <>
                          Resenärens telefonnummer <span>(Obligatoriskt)</span>
                        </>
                      }
                      type="text"
                      autocomplete={"text"}
                      numbersOnly
                    />

                    <Heading as="h3" id="haveFiduciaryHeading">
                      Vårdnadshavare/förälder/god man/ombud
                    </Heading>

                    <p>
                      För att kunna behandla överklagan behöver du uppge en
                      mejladress till en vårdnadshavare/förälder, god man eller
                      ombud, som är över 18 år.
                    </p>

                    <InputText
                      name="reporterEmail"
                      label={
                        <>
                          Mejladress för kontakt <span>(Obligatoriskt)</span>
                        </>
                      }
                      type="email"
                      autocomplete={"email"}
                    />
                  </>
                )}
              </Box>

              <div className="flex justify-center">
                <div className="w-full text-left flex items-center">
                  <Button
                    iconPosition="left"
                    iconSize={16}
                    iconName="arrow-left"
                    aria-label="Tillbaka"
                    showIcon
                    size="medium"
                    variant="outline"
                    onClick={(event) => {
                      event.preventDefault();
                      const backUrl = undefined;
                      if (backUrl) {
                        navigate(backUrl, { replace: true });
                      } else {
                        navigate(-1);
                      }
                    }}
                  >
                    Tillbaka
                  </Button>
                </div>
                <div className="w-full text-right">
                  <Button onClick={handleSubmitClick(formRef)} type="submit">
                    Gå vidare
                  </Button>
                </div>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
}
